import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  SimpleChanges,
  ViewEncapsulation
} from '@angular/core';
import { IonicModule } from '@ionic/angular';
import { SmartScore } from 'src/app/services/analyse/analyse.types';
import { ScoreIconComponent } from './icons/score-icon.component';
import { IconInfoComponent } from './icons/icon-info.component';
import { smartscoreGrades } from './utils';
import { hasChanged } from 'src/app/utils/change-utils';

@Component({
  selector: 'app-smartscore',
  standalone: true,
  imports: [CommonModule, IonicModule, ScoreIconComponent, IconInfoComponent],
  template: `
    <ion-row class="analyze-consumption-smartscore [--_score-w:2rem]">
      <ion-col>
        <div class="mb-4">
          <div class="mb-3">
            <h2 class="my-0">
              @if (!isPastSmartScore) { Slik bruker du strøm } @else { Resultat
              for {{ prevMonthLabel }} {{ prevMonthYearLabel }} }
              <ion-icon
                name="help-circle-outline"
                class="translate-y-[.125em] px-1 hover:cursor-pointer"
                [attr.id]="'icon-info-' + modalId"
              ></ion-icon>
            </h2>
          </div>

          <ion-modal [trigger]="'icon-info-' + modalId">
            <ng-template>
              <app-icon-info></app-icon-info>
            </ng-template>
          </ion-modal>

          @if (!hasError && smartScore) {
          <div class="isolate grid gap-5">
            <div
              class="flex justify-evenly rounded-full border border-[--primary-strong]"
            >
              @for (x of grading; track x.level) {
              <p
                class="app-score-icon m-0 smartscore inline-block px-1 pt-3 pb-2 relative before:content-[''] before:absolute before:z--1 after:absolute after:z-2 after:text-center"
                [class.current]="x.level === grade.level"
              >
                <app-score-icon
                  [score]="x.level"
                  class="contents"
                ></app-score-icon>
              </p>
              }
            </div>

            <div class="border-l-[3px] pl-4 border-[var(--lysning)] grid gap-1">
              <p class="text-xl m-0">
                <strong>{{ grade.name }}</strong>
              </p>
              @if(showDescription) {
              <p class="m-0">{{ grade.description }}</p>
              }
            </div>
          </div>
          } @else {
          <p class="bg-[--warning-40] p-4 rounded-lg mt-0">
            Det skjedde dessverre en feil når vi prøvde å hente analysen. Prøv
            igjen senere.
          </p>
          }
        </div>
      </ion-col>
    </ion-row>
  `,
  encapsulation: ViewEncapsulation.None,
  styles: `
    .analyze-consumption-smartscore .smartscore {
      & :is(img, svg) {
        width: var(--_score-w);
        height: auto;
        position: relative;
        z-index: 1;
      }

      &.current :is(img, svg) {
        --smartscore-drop-shadow: 0 -.375em .5em hsl(0 0% 100% / .75);
        filter: drop-shadow(0 -.5em .75em hsl(0 0% 100% / .875));
        /* animate slow pulse, once */
        animation: animate-current-smartscore 1s .5s 1 forwards;
      }

      &.current::before {
        --_blur: calc(var(--_score-w) / 12);
        inset: auto var(--_blur) 7.5%;
        /* Radial gradient bg */
        background: radial-gradient(ellipse at center, hsl(0 0% 0% / .875), hsl(0 0% 0% / .25)) no-repeat;
        border-radius: 50%;
        box-shadow: 0 0 var(--_blur) black;
        height: calc(var(--_score-w) * .2);
        opacity: .25;
        scale: .5;
        animation: animate-smartscore-shadow 1s .5s 1 forwards;
      }

      &.current::after {
        content: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" width="20" height="20"><polygon points="10,5 5,15 15,15" fill="currentcolor" transform="rotate(180 10 10)"/></svg>');
        inset: calc(var(--_score-w) * -.3) 0 auto;
        opacity: 0;
        /* animate slide-in-current-smartcore-arrow, once, keep end-state */
        animation: slide-in-current-smartcore-arrow 1s .5s 1 forwards;
      }
    }

    @keyframes slide-in-current-smartcore-arrow {
      0% {
        opacity: 0;
        transform: translateX(-1.5em);
      }
      100% {
        opacity: 1;
        transform: translateX(0);
      }
    }

    @keyframes animate-current-smartscore {
      0% { filter: drop-shadow(0 -.5em .75em hsl(0 0% 100% / .875)); }
      100% { filter: drop-shadow(var(--smartscore-drop-shadow)); }
    }

    @keyframes animate-smartscore-shadow {
      0% { scale: .5; }
      100% { scale: 1; }
    }
  `
})
export class SmartScoreComponent {
  @Input() smartScore: SmartScore;
  @Input() showDescription: boolean;
  @Input() hasError: boolean;
  @Input() isPastSmartScore: boolean;

  grading = smartscoreGrades;
  grade: { level: number; name: string; description: string };
  modalId: string;

  prevMonthLabel: string;
  prevMonthYearLabel: string;

  constructor() {
    this.modalId = Math.random().toString().substring(2);
  }

  ngOnInit() {
    const now = new Date();
    this.prevMonthLabel = new Date(
      now.setMonth(now.getMonth() - 1)
    ).toLocaleDateString('nb-NO', { month: 'long' });
    this.prevMonthYearLabel = new Date(
      now.setMonth(now.getMonth() - 1)
    ).toLocaleDateString('nb-NO', { year: 'numeric' });

    if (this.smartScore) {
      this.grade = this.grading.find((g) => g.level === this.smartScore.level);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (hasChanged([changes.smartScore])) {
      this.smartScore = changes.smartScore.currentValue;
      this.grade = this.grading.find((g) => g.level === this.smartScore.level);
    }
  }
}
